import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Helper function to detect coordinates
const isCoordinates = (text: string): boolean => {
  // Check for coordinate patterns like "-1.2864, 36.8172" or "lat,lng"
  const coordRegex = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
  return coordRegex.test(text.trim());
};

export interface BookingDetails {
  id: number;
  service_id: number;
  customer_id: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_map_link?: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  status: string;
  notes?: string;
  note?: string;
  decline_reason?: string;
  created_at: string;
  updated_at: string;
  completion_notes?: string;
  accepted_at?: string;
  service: {
    id: number;
    name: string;
    price?: number;
    owner_id: string;
    location?: string;
  };
  customer: {
    id: string;
    email: string;
    name?: string;
    phone?: string;
    directions?: string;
  };
  vendor?: {
    id: string;
    email: string;
    name?: string;
    phone?: string;
  };
  completion?: {
    id: string;
    comment?: string;
    rating: number;
    payment_amount: number;
    payment_status: string;
    created_at: string;
  };
}

export const useBookingDetails = (bookingId: string | undefined) => {
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchBookingDetails = async () => {
    if (!bookingId) {
      setError('No booking ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching booking details for ID:', bookingId);

      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select(`
          *,
          service:services (
            id,
            name,
            price,
            owner_id,
            location
          )
        `)
        .eq('id', parseInt(bookingId))
        .maybeSingle();

      if (fetchError) {
        console.error('❌ Error fetching booking details:', fetchError);
        setError('Failed to load booking details');
        return;
      }

      console.log('📊 Booking query result:', data);

      if (!data) {
        console.log('❌ No booking found for ID:', bookingId);
        setError('Booking not found');
        return;
      }

      console.log('✅ Booking found:', data);

      // Fetch customer details
      let customerData = null;
      if (data.customer_id) {
        const { data: customer } = await supabase
          .from('profiles')
          .select('id, email, display_name, photo_url, location, address')
          .eq('id', data.customer_id)
          .single();
        customerData = customer;
        
        // Also fetch notification preferences to get phone number
        const { data: notificationPrefs } = await supabase
          .from('notification_preferences')
          .select('notification_phone')
          .eq('user_id', data.customer_id)
          .single();
        
        if (notificationPrefs?.notification_phone) {
          customerData = { ...customerData, phone: notificationPrefs.notification_phone };
        }
      }

      // Fetch vendor details if it's not the customer's own booking
      let vendorData = null;
      if (data.service?.owner_id) {
        const { data: vendor } = await supabase
          .from('profiles')
          .select('id, email, display_name, photo_url')
          .eq('id', data.service.owner_id)
          .single();
        vendorData = vendor;
      }

      // Fetch completion data if booking is completed
      let completionData = null;
      if (data.status === 'completed') {
        const { data: completion, error: completionError } = await supabase
          .from('booking_completions')
          .select('id, comment, rating, payment_amount, payment_status, created_at')
          .eq('booking_id', data.id)
          .maybeSingle();
        
        if (!completionError && completion) {
          completionData = completion;
        }
      }

      const bookingDetails: BookingDetails = {
        ...data,
        customer: {
          id: customerData?.id || data.customer_id || '',
          email: customerData?.email || data.customer_email || '',
          name: customerData?.display_name || data.customer_name || '',
          phone: customerData?.phone || data.customer_phone || '', // Prioritize phone from profile
          directions: customerData?.location || '', // Use location field from profile for directions
        },
        customer_address: customerData?.address || data.customer_address || '',
        customer_map_link: customerData?.location && isCoordinates(customerData.location) ? customerData.location : null,
        vendor: vendorData ? {
          id: vendorData.id,
          email: vendorData.email,
          name: vendorData.display_name || '',
          phone: '', // No phone field in profiles table
        } : undefined,
        completion: completionData || undefined,
      };

      setBooking(bookingDetails);
    } catch (err) {
      console.error('Error in fetchBookingDetails:', err);
      setError('An unexpected error occurred');
      toast({
        title: "Error",
        description: "Failed to load booking details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  return {
    booking,
    loading,
    error,
    refetch: fetchBookingDetails,
  };
};